import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../services/api";
import {
  login as persistLogin,
  logout as persistLogout,
  client,
} from "../../services/auth";
import { useAuth } from "../../contexts/AppContext";
import { clearBannerPopupsSeen } from "../../utils/bannerPopupSeen";
import { getApiErrorMessage } from "../../utils/handleApiError";
import { ClientData } from "../../types/auth";
import { LoginResponse } from "../../types/api/AuthResponse";

interface LoginParams {
  token: string;
  coreIndustry: string;
}

export const useLogin = () => {
  const queryClient = useQueryClient();
  const { setLoggedIn, setLoading, setError, setClientData } = useAuth();

  return useMutation<LoginResponse, unknown, LoginParams>({
    mutationFn: async ({ token, coreIndustry }) => {
      setLoading(true);
      setError(null);
      // Persiste o token primeiro
      persistLogin(token);

      const response = await api.post<LoginResponse>("cliente/valida-sessao", {
        coreIndustry,
      });

      if (response.status !== 200) {
        // Se falhar, remove o token
        persistLogout();
        throw new Error("Não foi possível autenticar");
      }

      return response.data;
    },
    onSuccess: data => {
      clearBannerPopupsSeen();

      // A API retorna dados compatíveis com ClientData
      const clientData = data as unknown as ClientData;

      // Salva os dados do cliente no localStorage
      client(clientData);

      // Atualiza o contexto de autenticação
      setClientData(clientData);
      setLoggedIn(true);
      setLoading(false);
      setError(null);

      // Invalida todas as queries para forçar recarregamento após login
      queryClient.invalidateQueries();
    },
    onError: (error: unknown) => {
      // Garante que o token seja removido em caso de erro
      persistLogout();

      // Atualiza o contexto com erro
      const errorMessage = getApiErrorMessage(
        error,
        "Não foi possível autenticar"
      );

      setError(errorMessage);
      setLoading(false);
      setLoggedIn(false);
      setClientData(null);
    },
  });
};
